/**
 * Excel downloads for the Reports tabs.
 *
 * Each workbook opens with the figures on screen and ends with the rows they
 * were totalled from, so anyone reconciling a number can find what is under it
 * without asking for another export.
 */
import ExcelJS from 'exceljs';
import { saveBlob } from './download.mjs';
import { periodLabel } from './format.mjs';
import { fundingByCode, claimTotals, claimsByRegion, claimsByPosition, claimsByPerson } from './aggregate.mjs';

const CARDINAL = 'FF8C1515';
const USD = '$#,##0.00';

const newBook = () => {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Stanford Seed Expense Claim System';
  wb.created = new Date();
  return wb;
};

/** A sheet with a title block and a styled header row. */
const sheet = (wb, name, title, subtitle, columns) => {
  const ws = wb.addWorksheet(name);
  ws.addRow([title]).font = { bold: true, size: 14, color: { argb: CARDINAL } };
  ws.addRow([subtitle]);
  ws.addRow([]);
  const head = ws.addRow(columns.map(c => c.label));
  head.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  head.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CARDINAL } };
  columns.forEach((c, i) => { ws.getColumn(i + 1).width = c.width || 16; });
  return ws;
};

/** Add rows, formatting the money columns. */
const addRows = (ws, columns, data) => {
  for (const values of data) {
    const row = ws.addRow(values);
    columns.forEach((c, i) => { if (c.money) row.getCell(i + 1).numFmt = USD; });
  }
};

const totalRow = (ws, columns, values) => {
  const row = ws.addRow(values);
  row.font = { bold: true };
  columns.forEach((c, i) => { if (c.money) row.getCell(i + 1).numFmt = USD; });
};

const save = async (wb, filename) => {
  const buffer = await wb.xlsx.writeBuffer();
  saveBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename
  );
};

const scopeText = (fyLabel, region, basis = 'finally approved reports only') => `${fyLabel} - ${region === 'all' ? 'all regions' : region}`
  + ` - ${basis} - exported ${new Date().toLocaleString('en-US')}`;

const FINAL_CLAIMS = 'finally approved claims only';

const typeLabel = (c) => (c.claimType === 'perdiem' ? 'Per diem' : 'Expense');

const fileScope = (fyShort, region) => `${fyShort}_${region === 'all' ? 'All_Regions' : region}`;

/* ---------------------------------------------------------------- funding */
export const exportFunding = async ({ lines, ptaes, fyLabel, fyShort, region }) => {
  const wb = newBook();
  const sub = scopeText(fyLabel, region);
  const codes = fundingByCode(lines, ptaes);

  const codeCols = [
    { label: 'PTAE', width: 30 }, { label: 'Description', width: 40 }, { label: 'Fund', width: 24 },
    { label: 'Region', width: 18 }, { label: 'Claims', width: 10 }, { label: 'Amount', money: true },
    { label: 'Share', width: 10 },
  ];
  const byCode = sheet(wb, 'By PTAE', 'FUNDING BY PTAE', sub, codeCols);
  addRows(byCode, codeCols, codes.map(c => [
    c.code || 'NOT ASSIGNED', c.description || '', c.fundName || '', c.region, c.claims, c.amount,
    `${(c.share * 100).toFixed(1)}%`,
  ]));
  totalRow(byCode, codeCols, ['TOTAL', '', '', '', '', codes.reduce((t, c) => t + c.amount, 0), '']);

  const lineCols = [
    { label: 'Period', width: 16 }, { label: 'Region', width: 16 }, { label: 'Claimant', width: 28 },
    { label: 'Type', width: 12 }, { label: 'PTAE', width: 30 }, { label: 'Amount', money: true },
    { label: 'Report ID', width: 38 },
  ];
  const detail = sheet(wb, 'Funding lines', 'FUNDING LINES', sub, lineCols);
  addRows(detail, lineCols, lines.map(l => [
    periodLabel(l.month, l.year), l.region, l.userName, l.claimType === 'per_diem' ? 'Per diem' : 'Expense',
    l.code || 'NOT ASSIGNED', l.amount, l.reportId,
  ]));

  await save(wb, `Funding_by_PTAE_${fileScope(fyShort, region)}.xlsx`);
};

/** The rows under every figure: one line per final claim. */
const addClaimsSheet = (wb, sub, claims) => {
  const cols = [
    { label: 'Date', width: 12 }, { label: 'Claimant', width: 28 }, { label: 'Region', width: 16 },
    { label: 'Job position', width: 24 }, { label: 'Type', width: 10 }, { label: 'Amount (USD)', money: true, width: 16 },
    { label: 'Final approval', width: 14 }, { label: 'Report', width: 28 }, { label: 'Claim #', width: 12 },
  ];
  const ws = sheet(wb, 'Claims', 'FINAL CLAIMS', sub, cols);
  addRows(ws, cols, (claims || []).map(c => [
    c.date || '', c.claimant?.name || '', c.claimant?.region || '', String(c.claimant?.position || '').trim(),
    typeLabel(c), Number(c.amountUsd) || 0,
    c.finalApprovedAt ? new Date(c.finalApprovedAt).toLocaleDateString('en-US') : '',
    c.reportLabel || (c.reportId ? '' : 'Not in a monthly report'),
    String(c.claimId || '').slice(-8).toUpperCase(),
  ]));
};

/* -------------------------------------------------------------- breakdown */
/**
 * The Breakdown tab: totals, by region and by job position, then every claim
 * they were totalled from.
 */
export const exportBreakdown = async ({ claims, regions = [], fyLabel, fyShort, region }) => {
  const wb = newBook();
  const sub = scopeText(fyLabel, region, FINAL_CLAIMS);
  const t = claimTotals(claims);

  const sumCols = [{ label: 'Figure', width: 34 }, { label: 'Value', width: 20 }];
  const sum = sheet(wb, 'Summary', 'APPROVED SPEND', sub, sumCols);
  sum.addRow(['Approved spend (USD)', t.total]).getCell(2).numFmt = USD;
  sum.addRow(['Per diem (USD)', t.perDiem]).getCell(2).numFmt = USD;
  sum.addRow(['Expense reimbursements (USD)', t.expense]).getCell(2).numFmt = USD;
  sum.addRow(['Claims', t.claims]);
  sum.addRow(['Claimants', t.claimants]);

  const groupCols = (first) => [
    { label: first, width: 30 }, { label: 'Claimants', width: 12 }, { label: 'Claims', width: 10 },
    { label: 'Per diem (USD)', money: true, width: 16 }, { label: 'Expense (USD)', money: true, width: 16 },
    { label: 'Total (USD)', money: true, width: 16 },
  ];
  const regionCols = groupCols('Region');
  const byRegion = sheet(wb, 'By region', 'APPROVED SPEND BY REGION', sub, regionCols);
  addRows(byRegion, regionCols, claimsByRegion(claims, regions).map(r => [
    r.region, r.claimants, r.claims, r.perDiem, r.expense, r.total,
  ]));
  totalRow(byRegion, regionCols, ['TOTAL', t.claimants, t.claims, t.perDiem, t.expense, t.total]);

  const posCols = groupCols('Job position');
  const byPos = sheet(wb, 'By job position', 'APPROVED SPEND BY JOB POSITION', sub, posCols);
  addRows(byPos, posCols, claimsByPosition(claims).map(p => [
    p.position, p.claimants, p.claims, p.perDiem, p.expense, p.total,
  ]));
  totalRow(byPos, posCols, ['TOTAL', t.claimants, t.claims, t.perDiem, t.expense, t.total]);

  addClaimsSheet(wb, sub, claims);
  await save(wb, `Approved_Spend_${fileScope(fyShort, region)}.xlsx`);
};

/* ------------------------------------------------------- claims by person */
/** Claims by person: one line per claimant, then every claim under them. */
export const exportClaimsByPerson = async ({ claims, fyLabel, fyShort, region }) => {
  const wb = newBook();
  const sub = scopeText(fyLabel, region, FINAL_CLAIMS);
  const people = claimsByPerson(claims);

  const personCols = [
    { label: 'Claimant', width: 28 }, { label: 'Region', width: 18 }, { label: 'Job position', width: 26 },
    { label: 'Claims', width: 10 }, { label: 'Per diem claims', width: 16 }, { label: 'Expense claims', width: 16 },
    { label: 'Total (USD)', money: true, width: 16 },
  ];
  const byPerson = sheet(wb, 'By person', 'FINAL CLAIMS BY PERSON', sub, personCols);
  addRows(byPerson, personCols, people.map(p => [
    p.name, p.region, p.position, p.claims, p.perDiemClaims, p.expenseClaims, p.total,
  ]));
  const t = claimTotals(claims);
  totalRow(byPerson, personCols, ['TOTAL', '', '', t.claims, t.perDiemClaims, t.expenseClaims, t.total]);

  addClaimsSheet(wb, sub, people.flatMap(p => p.list));
  await save(wb, `Claims_by_Person_${fileScope(fyShort, region)}.xlsx`);
};
